import CustomCard from './CustomCard';
import { CustomMdChip } from './Styles';
import { DownloadIcon, FileChip, IconCloseChip } from './buttons/IconButtons';
import { useTranslation } from 'react-i18next';
import ButtonAction from './buttons/ButtonAction';
import { statusBorderColor, statusColor, statusFontColor } from '../enums/statusColorEnum';
import CustomChip from './CustomChip';
import { status, STATUS_MODEL } from '../enums/statusEnum';
import CustomTooltip from './CustomTooltip';
import React from 'react';
import { POSITION_MODEL } from "../Helpers/PositionModel";

/**
 * @Email    : kpozos@ibl.mx
 * @Author   : Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 *
 * CustomFileCard component
 *
 * This component renders a styled card that displays:
 * - An icon or default file chip
 * - Title and subtitle information
 * - A download button if a file is available
 * - Otherwise, a status chip with "Pending" state (frontend only)
 *
 * @component
 * @param {string} buttonId - Unique identifier used for the download button element ID  (required).
 * @param {React.Component} Icon - Custom icon to display (default is FileChip) (optional).
 * @param {string} title - Main title text (required).
 * @param {string|boolean} fileValue - If truthy, shows the download button; otherwise shows a pending status. (optional)
 * @param {string|JSX.Element} subtitle - Subtitle or additional description (optional).
 * @param {Function} onClick - Callback function executed when download button is clicked (required).
 * @param {Function} clickBtnDelete - Event to delete file
 * @param {boolean} showBtnDelete - If true, the delete icon is displayed in the file
 * @param {String} classContent: Classes to apply to the CardContent section, usually used to define the padding/margin of the card (optional)
 * @param String classes: Additional classes to apply to the outer wrapper div, usually used to define the height of the card (optional)
 * @param {boolean} showBtnDownload: If true, the download icon is displayed
 * @param {boolean} showChip: If true, the chip icon is displayed
 * @param {React.ComponentType|JSX.Element|null} [downloadIcon=null] - Custom icon used for the download button (optional).
 * @param {React.ComponentType|JSX.Element|null} [deleteIcon=null] - Custom icon used for the delete button (optional).
 * @param String classes: Additional classes to apply to the outer wrapper div, usually used to define the height of the card (optional)
 * @param {boolean} [disabledBtnDownload=false] - Disables the download button and applies disabled styles when true.
 * @param {JSX.Element|null} [footer=null] - Optional footer element displayed at the bottom of the card.
 * @param {JSX.Element} extraActions Rendered of extra action buttons after the default download/delete buttons.
 * @param {boolean} [showIcon=true] - If true, displays the main icon (Icon or default FileChip) on the left.
 * @param {string|number} statusId - Status identifier used to determine chip colors and translated label.
 *
 * @param {boolean} emptyFields - Indicates whether the input is flagged as empty/invalid (required).
 * @param {boolean} showTooltip - If you need to display showTooltip, send the variable as true (optional)
 * @param {string} textTooltip - If a different text than "download" is required, please send different text (optional)
 * @param {string} placement: Position for tooltip (optional)
 * @returns {JSX.Element} Rendered CustomFileCard component.
 */
const CustomFileCard = ({
    buttonId = 'btn_cfc_download',
    Icon,
    title,
    fileValue,
    subtitle,
    onClick = null,
    clickBtnDelete = null,
    showBtnDelete = false,
    classContent = '',
    showBtnDownload = true,
    showChip = false,
    downloadIcon = null,
    deleteIcon = null,
    classes = 'card-shades-100 border-radius-8px',
    showIcon = true,
    disabledBtnDownload = false,
    footer,
    downloadPosition = POSITION_MODEL.RIGHT, // LEFT | RIGHT
    statusId = STATUS_MODEL.PENDING,
    extraActions,
    emptyFields = false,
    showTooltip=false,
    textTooltip='download',
    placement='bottom',
    required = false
}) => {
    const { t } = useTranslation();
    const resolvedStatusId = statusId && status[statusId] ? statusId : STATUS_MODEL.PENDING;

    /**
     * Renders the download button if a file is available and the download action is enabled.
     *
     * The button displays a custom icon if provided; otherwise, it uses the default download icon.
     * The button is disabled visually and functionally when `disabledBtnDownload` is true.
     *
     * @returns {JSX.Element | false} Download button component or `false` if conditions are not met.
     */
    const renderDownloadButton = () =>
        fileValue &&
        showBtnDownload && (() => {
            const button = (
                <ButtonAction
                    Icon={downloadIcon ? downloadIcon : DownloadIcon}
                    id={buttonId}
                    className={`w-40px h-40px minw-40px p-0 text-ellipsis ${
                        disabledBtnDownload
                            ? 'background-shades-200 cursor-not-allowed'
                            : 'btn-outlined-secondary-600-25 btn-icon-svg-chip-button'
                    }`}
                    iconProps="icon"
                    iconClass="mr-0"
                    onClick={onClick}
                />
            );

            if (!showTooltip) return button;

            return (
                <CustomTooltip
                    text={t(textTooltip)}
                    placement={placement}
                >
                    <span className="d-inline-flex">
                        {button}
                    </span>
                </CustomTooltip>
            );
        })();

    /**
     * Renders the delete button when the delete action is enabled.
     *
     * If a custom delete icon is provided, it renders a `ButtonAction` component.
     * Otherwise, it renders the default `IconCloseChip` component.
     * The click event is safely executed only if the `clickBtnDelete` handler exists.
     *
     * @returns {JSX.Element | false} Delete button component or `false` if the button should not be displayed.
     */
    const renderDeleteButton = () =>
        showBtnDelete &&
            (deleteIcon ? (
                <ButtonAction
                    Icon={deleteIcon}
                    id='btn_cfc_delete'
                    className={'btn-icon-svg-chip-button btn-outlined-error-600 w-40px h-40px minw-40px p-0'}
                    iconClass={'mr-0'}
                    onClick={(e) => clickBtnDelete && clickBtnDelete(e)}
                />
        ) : (
            <IconCloseChip onClick={(e) => clickBtnDelete && clickBtnDelete(e)} />
    ));

    /**
     * Renders a status chip when the chip display is enabled.
     *
     * The chip represents the "Pending" status using predefined background,
     * font, and border colors. If the translation is not available, a default
     * empty value is displayed as the legend.
     *
     * @returns {JSX.Element | false} Status chip component or `false` if it should not be rendered.
     */
    const renderChip = () =>
        showChip && (
            <div className='pl-3 pr-3'>
                <CustomChip
                    variant={'large'}
                    background={statusColor[resolvedStatusId]}
                    fontColor={statusFontColor[resolvedStatusId]}
                    borderColor={statusBorderColor[resolvedStatusId]}
                    legend={t(status[resolvedStatusId] ?? 'pending')}
                />
            </div>
        );
    return (
        <CustomCard
            classes={classes}
            classContent={classContent}
            children={
                 <div className="d-flex flex-column p-0 h-100">
                    <div className={'d-flex justify-content-start align-items-center p-0'}>
                        {showIcon && (
                            <div className='px-2'>
                                {Icon ? <Icon style={CustomMdChip.icon} /> : <FileChip style={CustomMdChip.icon} />}
                            </div>
                        )}
                        <div className='flex-grow-1 minw-0 ml-2'>
                            <div className={'flex-grow-1 minw-0 flex-row'}>
                                <CustomTooltip
                                    text={title }
                                    classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis-1-row`}
                                    showIfTruncated={true}
                                    tooltipClasses={"maxw-100"}
                                />
                                {emptyFields && required ? <p className={"d-flex align-items-start font-color-error-600"}>{' *'}</p> : (required  ? ' *' :  '') }
                                </div>
                                {subtitle}
                            </div>

                            <div className={'d-flex-end gap-2'}>
                                {downloadPosition === POSITION_MODEL.LEFT && renderDownloadButton()}
                                {renderDeleteButton()}
                                {renderChip()}
                                {downloadPosition === POSITION_MODEL.RIGHT && renderDownloadButton()}
                                {extraActions &&
                                    extraActions
                                }
                            </div>
                        </div>
                     {footer && <div className='mt-auto'>{footer}</div>}
                 </div>
            }
        />
    );
}

export default CustomFileCard;
